// Sidebar do app (shadcn Sidebar). Navegação principal: substitui a antiga top-nav.
// Grupos colapsáveis (Monitoramento, Relatórios, Gestão) + itens diretos (Dashboard, Discador).
// O rodapé é reativo à sessão (Better Auth): logado mostra usuário + "Sair"; senão "Entrar" (-> /auth).
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useRouterState, type LinkProps } from "@tanstack/react-router";
import { LayoutGroup, motion } from "motion/react";
import type { LucideIcon } from "lucide-react";
import {
  Building2,
  ChevronRight,
  ChevronsUpDown,
  Download,
  LayoutDashboard,
  LogIn,
  LogOut,
  Moon,
  PhoneOutgoing,
  UserRound,
  Sun,
  Sparkles,
} from "lucide-react";
import { logout, useActiveOrganization, useSession } from "@/lib/auth-client";
import { usePreferences, type Theme } from "./preferences-store.js";
import { LanguageSubmenu } from "./language.js";
import { AppBrandMark } from "./brand.js";
import {
  AUDITORIA,
  CONFIGURACOES,
  clickToCallNavGroup,
  filterNavGroup,
  FILA,
  FINANCEIRO,
  GESTAO,
  INTEGRACOES,
  JORNADA_LINK,
  MONITORAMENTO,
  navTranslationKey,
  RELATORIOS,
  showInNav,
  SUPORTE,
  type NavGroupDef,
} from "./nav-config.js";
import { canAccess } from "./lib/access.js";
import { useClickToCallEnabled } from "./lib/click-to-call-enabled.js";
import { useSupportEnabled } from "./lib/support-enabled.js";
import { useInstallPrompt } from "./hooks/use-install-prompt.js";
import { useJourneyProgress } from "./hooks/use-journey-progress.js";
import { useAccessSnapshot } from "./hooks/use-viewer-role.js";
import { BrandDialog } from "./brand-dialog.js";
import { CHANGELOG_PAGE_URL } from "./changelog-service.js";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

function useTranslatedNavLabel(label: string): string {
  const { t } = useTranslation();
  const key = navTranslationKey(label);
  return key ? t(key) : label;
}

// Indicador que "persegue" a rota ativa. O layoutId compartilhado faz o framer deslizar
// o mesmo elemento de um item para outro. Cobre o botão inteiro; primary com opacity baixa.
function ActiveIndicator() {
  return (
    <motion.span
      layoutId="sidebar-active-indicator"
      className="absolute inset-0 z-0 rounded-md bg-primary/10 ring-1 ring-primary/15"
      transition={{ type: "spring", stiffness: 380, damping: 32 }}
    />
  );
}

// Dá um "pulse" no ícone quando o item vira ativo. A key remonta o motion.span a cada
// troca de estado, então o overshoot da mola dispara uma única vez na seleção (não a cada render).
function PulseIcon({ icon: Icon, active }: { icon: LucideIcon; active: boolean }) {
  return (
    <motion.span
      key={active ? "on" : "off"}
      className="relative z-10 flex"
      initial={{ scale: active ? 0.92 : 1 }}
      animate={{ scale: 1 }}
      transition={
        active ? { type: "spring", stiffness: 210, damping: 26, mass: 0.7 } : { duration: 0 }
      }
    >
      <Icon />
    </motion.span>
  );
}

function NavLinkItem({
  to,
  label,
  icon: Icon,
  active,
  badge,
}: {
  to: LinkProps["to"];
  label: string;
  icon: LucideIcon;
  active: boolean;
  // Contador do tipo "1/7" à direita do rótulo (hoje só a jornada usa).
  badge?: string;
}) {
  const translatedLabel = useTranslatedNavLabel(label);
  const { t } = useTranslation();
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={active}
        tooltip={badge ? `${translatedLabel} (${badge})` : translatedLabel}
        className="relative h-10 cursor-pointer overflow-visible data-[active=true]:bg-transparent data-[active=true]:text-primary"
      >
        <Link to={to}>
          {active && <ActiveIndicator />}
          <PulseIcon icon={Icon} active={active} />
          <span className="relative z-10 truncate">{translatedLabel}</span>
          {badge ? (
            // Some junto com o rótulo no modo de ícones: no trilho estreito não
            // cabe, e o tooltip já carrega o contador.
            //
            // `aria-label` num `<span>` sem role é IGNORADO pela ARIA (elemento
            // `generic`) — o dígito visível "1/7" era tudo que leitor de tela
            // lia. Padrão que funciona: dígito com `aria-hidden`, mais um
            // `sr-only` com o texto por extenso ao lado.
            <span className="relative z-10 ml-auto shrink-0 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold leading-none tabular-nums text-primary-foreground group-data-[collapsible=icon]:hidden">
              <span aria-hidden="true">{badge}</span>
              <span className="sr-only">
                {t("navigation.startHere")}: {badge}
              </span>
            </span>
          ) : null}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function ExternalNavItem({
  href,
  label,
  icon: Icon,
}: {
  href: string;
  label: string;
  icon: LucideIcon;
}) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild tooltip={label} className="h-10 cursor-pointer">
        <a href={href} target="_blank" rel="noreferrer">
          <Icon />
          <span>{label}</span>
        </a>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function NavGroupItem({ group, pathname }: { group: NavGroupDef; pathname: string }) {
  const translatedGroupLabel = useTranslatedNavLabel(group.label);
  const hasActive = group.items.some((i) => pathname === i.to);
  const { isMobile, open, setOpen } = useSidebar();

  function handleGroupClick() {
    // Em modo de ícones o conteúdo do grupo fica oculto pelo CSS. Abrir apenas
    // o Collapsible não revela nada, então expandimos primeiro a sidebar para
    // que o clique tenha um resultado visível e os subitens possam ser usados.
    if (!isMobile && !open) setOpen(true);
  }

  return (
    <Collapsible asChild defaultOpen={hasActive} className="group/collapsible">
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            tooltip={translatedGroupLabel}
            className="h-10 cursor-pointer"
            onClick={handleGroupClick}
          >
            <group.icon />
            <span>{translatedGroupLabel}</span>
            <ChevronRight className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {group.items.map((sub) => {
              const subActive = pathname === sub.to;
              const key = navTranslationKey(sub.label);
              return (
                <SidebarMenuSubItem key={sub.to}>
                  <SidebarMenuSubButton
                    asChild
                    isActive={subActive}
                    className="relative h-10 cursor-pointer overflow-visible data-[active=true]:bg-transparent data-[active=true]:text-primary"
                  >
                    <Link to={sub.to}>
                      {subActive && <ActiveIndicator />}
                      <span className="relative z-10">
                        {key ? (
                          <TranslatedNavLabel translationKey={key} fallback={sub.label} />
                        ) : (
                          sub.label
                        )}
                      </span>
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              );
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

function TranslatedNavLabel({
  translationKey,
  fallback,
}: {
  translationKey: string;
  fallback: string;
}) {
  const { t } = useTranslation();
  return t(translationKey, { defaultValue: fallback });
}

// Item do menu que abre o dialog de troca de marca. Mostra a marca ativa. O
// dialog em si é renderizado no SidebarUser (fora do dropdown), senão o Radix o
// desmontaria ao fechar o menu.
function OrgSwitcherItem({ onOpen }: { onOpen: () => void }) {
  const { t } = useTranslation();
  const { data: activeOrg } = useActiveOrganization();
  const activeOrgName = activeOrg?.name ?? null;

  return (
    <DropdownMenuItem
      onSelect={(e) => {
        // Impede o fechamento imediato do menu engolir a abertura do dialog.
        e.preventDefault();
        onOpen();
      }}
    >
      <Building2 />
      <span className={`flex-1 truncate${activeOrgName ? "" : " text-muted-foreground"}`}>
        {activeOrgName ?? t("common.selectBrand")}
      </span>
      <ChevronRight className="ml-auto size-4 text-muted-foreground" />
    </DropdownMenuItem>
  );
}

// Submenu de tema (claro/escuro), persistido no store de preferências.
function ThemeSubmenu() {
  const { t } = useTranslation();
  const theme = usePreferences((s) => s.theme);
  const setTheme = usePreferences((s) => s.setTheme);
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        {theme === "dark" ? <Moon /> : <Sun />}
        <span className="flex-1">{t("common.theme")}</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        <DropdownMenuRadioGroup value={theme} onValueChange={(v) => setTheme(v as Theme)}>
          <DropdownMenuRadioItem value="light">{t("common.themes.light")}</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">{t("common.themes.dark")}</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

// Rodapé reativo à sessão (Better Auth). Logado: avatar + dropdown com Perfil,
// Organização, Tema e Sair. Deslogado: "Entrar".
function SidebarUser() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: session, isPending } = useSession();
  const user = session?.user;
  const [brandsOpen, setBrandsOpen] = useState(false);
  const { isStandalone } = useInstallPrompt();

  const primary = user ? user.name || user.email : t("common.unauthenticated");
  const secondary = user
    ? user.email
    : isPending
      ? t("common.checkingSession")
      : t("common.clickToSignIn");
  const initials = (user ? user.name || user.email || "?" : "—").slice(0, 2).toUpperCase();

  async function handleSignOut() {
    await logout();
    void navigate({ to: "/auth" });
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton size="lg">
              <Avatar className="size-7 rounded-md">
                {user?.image ? <AvatarImage src={user.image} alt={`Avatar de ${primary}`} /> : null}
                <AvatarFallback className="rounded-md text-xs">{initials}</AvatarFallback>
              </Avatar>
              <div className="flex flex-1 flex-col text-left leading-tight">
                <span className="text-sm">{primary}</span>
                <span className="truncate text-xs text-muted-foreground">{secondary}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="top"
            align="start"
            className="w-(--radix-popper-anchor-width) min-w-56"
          >
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col leading-tight">
                <span className="text-sm">{primary}</span>
                <span className="truncate text-xs text-muted-foreground">{secondary}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {user ? (
              <>
                <DropdownMenuItem asChild>
                  <Link to="/profile">
                    <UserRound />
                    {t("common.profile")}
                  </Link>
                </DropdownMenuItem>
                <OrgSwitcherItem onOpen={() => setBrandsOpen(true)} />
                {/* Some quando o app já roda instalado — mesma regra do convite
                    em /profile e da linha no drawer do mobile. */}
                {isStandalone ? null : (
                  <DropdownMenuItem asChild>
                    <Link to="/install">
                      <Download />
                      {t("common.installApp")}
                    </Link>
                  </DropdownMenuItem>
                )}
                <ThemeSubmenu />
                <LanguageSubmenu />
                {/* "Configurações" saiu daqui: virou grupo próprio na navegação
                    (CONFIGURACOES), com uma rota por assunto. */}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut}>
                  <LogOut />
                  {t("common.signOut")}
                </DropdownMenuItem>
              </>
            ) : (
              <DropdownMenuItem asChild>
                <Link to="/auth">
                  <LogIn />
                  {t("common.signIn")}
                </Link>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        {user ? <BrandDialog open={brandsOpen} onOpenChange={setBrandsOpen} /> : null}
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export function AppSidebar() {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const access = useAccessSnapshot();
  // O grupo "Click to call" só aparece com o recurso habilitado na org; a tela
  // que o habilita fica sempre em Integrações.
  const clickToCallEnabled = useClickToCallEnabled(
    canAccess({ ...access, path: "/management/click-to-call-audit" }),
  );
  const clickToCall = clickToCallNavGroup(clickToCallEnabled);
  // "Meus chamados" só existe no menu quando o back tem as envs do Jira
  // configuradas (GET /v1/support/config). Sem isso o item ofereceria uma
  // tela que só dá 503 — a mesma regra que help-screen.tsx e
  // assistant-screen.tsx já aplicam ao botão "Abrir chamado".
  const supportEnabled = useSupportEnabled();
  // Jornada de primeiro acesso: enquanto sobrar passo acionável, o item sai do
  // fim da lista e vira o PRIMEIRO, com o contador. Concluída (ou sem dado
  // ainda), volta a ser um item comum lá embaixo.
  const journey = useJourneyProgress();
  const jornadaPendente = !!journey?.pendente;
  // Grupo Suporte: com o suporte desligado (sem envs do Jira no back), some
  // só "Meus chamados" — a Ajuda (FAQ + assistente) existe sem Jira.
  const grupoSuporte = {
    ...SUPORTE,
    items: supportEnabled
      ? SUPORTE.items
      : SUPORTE.items.filter((i) => i.to !== "/support/tickets"),
  };
  // O vendedor não vê a jornada (nem destacada, nem no fim da lista).
  const jornadaVisivel = showInNav(access, JORNADA_LINK.to as string);
  const jornadaItem = jornadaVisivel ? (
    <NavLinkItem
      to={JORNADA_LINK.to}
      label={JORNADA_LINK.label}
      icon={JORNADA_LINK.icon}
      active={pathname.startsWith("/jornada")}
      badge={jornadaPendente && journey ? `${journey.done}/${journey.total}` : undefined}
    />
  ) : null;

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="px-2 py-3">
        <div className="flex items-center justify-between gap-2 group-data-[collapsible=icon]:justify-center">
          {/* Logo + wordmark somem juntos no modo ícone: sobra só o trigger,
              que é o único alvo clicável de uma sidebar de 3rem. */}
          <AppBrandMark className="group-data-[collapsible=icon]:hidden" />
          <SidebarTrigger className="size-8 shrink-0" />
        </div>
      </SidebarHeader>

      <SidebarContent>
        <LayoutGroup>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu className="gap-1.5">
                {jornadaPendente && jornadaItem}
                {showInNav(access, "/dashboard") && (
                  <NavLinkItem
                    to="/dashboard"
                    label={t("navigation.dashboard")}
                    icon={LayoutDashboard}
                    active={pathname.startsWith("/dashboard")}
                  />
                )}
                {canAccess({ ...access, path: "/dialer" }) && (
                  <NavLinkItem
                    to="/dialer"
                    label={t("navigation.automaticDialer")}
                    icon={PhoneOutgoing}
                    active={pathname.startsWith("/dialer")}
                  />
                )}
                {/* "Discagem manual" (/phone) escondida da navegação: o fluxo é o
                    discador automático. A rota continua válida para links diretos. */}
                {[
                  MONITORAMENTO,
                  RELATORIOS,
                  AUDITORIA,
                  GESTAO,
                  FILA,
                  FINANCEIRO,
                  ...(clickToCall ? [clickToCall] : []),
                  INTEGRACOES,
                  CONFIGURACOES,
                  grupoSuporte,
                ].map((g) => {
                  const fg = filterNavGroup(g, access);
                  return fg ? <NavGroupItem key={fg.label} group={fg} pathname={pathname} /> : null;
                })}
                {!jornadaPendente && jornadaItem}
                <ExternalNavItem
                  href={CHANGELOG_PAGE_URL}
                  label={t("navigation.whatsNew")}
                  icon={Sparkles}
                />
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </LayoutGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarUser />
      </SidebarFooter>
    </Sidebar>
  );
}
